import { ChevronDown, ChevronRight, Send } from "lucide-react";
import { useState } from "react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { useCouncilAsk, useCouncilMembers } from "@/sdk/hooks/use-council";
import type { CouncilMemberWire, CouncilVerdictWire } from "@/sdk/hooks/use-council";

// Backend CouncilAskRequest enforces min_length=5 — gate client-side too so
// a short question fails loudly here instead of as an opaque 422.
const MIN_QUESTION_LENGTH = 5;

function MemberCard({ member }: { member: CouncilMemberWire }) {
  const [expanded, setExpanded] = useState(false);
  const pct = Number.isFinite(member.confidence)
    ? Math.round(member.confidence * 100)
    : null;

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between"
        onClick={() => setExpanded((value) => !value)}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-white/60">{member.name}</span>
          <span className="text-xs text-white/40">{member.model}</span>
          {member.error ? (
            <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-[0.65rem] font-semibold text-red-300">
              error
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {pct != null && !member.error ? (
            <span className="text-sm font-bold text-white/80">{pct}% conf</span>
          ) : null}
          {expanded ? <ChevronDown className="h-3 w-3 text-white/40" /> : <ChevronRight className="h-3 w-3 text-white/40" />}
        </div>
      </button>
      {expanded ? (
        <div className="mt-2 space-y-1.5">
          {member.error ? (
            <p className="text-xs leading-relaxed text-red-300/90">{member.error}</p>
          ) : (
            <>
              {member.reasoning ? (
                <p className="text-xs leading-relaxed text-white/70">{member.reasoning}</p>
              ) : null}
              {member.response ? (
                <p className="border-l-2 border-white/15 pl-2 text-xs leading-relaxed text-white/50">
                  {member.response}
                </p>
              ) : null}
              {member.search_used ? (
                <p className="text-[0.65rem] text-white/30">used web search</p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function VerdictView({ verdict }: { verdict: CouncilVerdictWire }) {
  const pct = Number.isFinite(verdict.confidence)
    ? Math.round(verdict.confidence * 100)
    : null;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wide text-white/40">Consensus</p>
          {pct != null ? (
            <span className="text-xs text-white/40">{pct}% confidence</span>
          ) : null}
        </div>
        <p className="mt-1 border-l-2 border-white/20 pl-3 text-sm leading-relaxed text-white/85">
          {verdict.consensus_answer}
        </p>
      </div>

      {verdict.key_points.length > 0 ? (
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-white/40">Key points</p>
          <ul className="list-disc space-y-0.5 pl-5 text-xs text-white/70">
            {verdict.key_points.map((point, index) => (
              <li key={index}>{point}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {verdict.dissenting_views.length > 0 ? (
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-amber-400/70">Dissenting views</p>
          <ul className="list-disc space-y-0.5 pl-5 text-xs text-amber-200/70">
            {verdict.dissenting_views.map((view, index) => (
              <li key={index}>{view}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-white/40">Member responses</p>
        {verdict.member_responses.map((member, index) => (
          <MemberCard key={`${member.name}-${index}`} member={member} />
        ))}
      </div>
    </div>
  );
}

export function CouncilPanel() {
  const [question, setQuestion] = useState("");
  const [verdict, setVerdict] = useState<CouncilVerdictWire | null>(null);
  const ask = useCouncilAsk();
  const members = useCouncilMembers();

  const trimmed = question.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_QUESTION_LENGTH;

  const handleAsk = () => {
    if (trimmed.length < MIN_QUESTION_LENGTH) return;
    ask.mutate({ question: trimmed }, { onSuccess: (value) => setVerdict(value) });
  };

  return (
    // This is the LEGACY multi-provider council (/api/council/* — GPT, Gemini,
    // Grok queried directly), not the alpha Tier-3 debate council. The title
    // stays explicit so the two are never conflated on the same page.
    <Panel
      title="Ask the Council (legacy multi-provider)"
      description="Direct GPT/Gemini/Grok consensus via /api/council/* — separate from the alpha debate council audit trail below."
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <textarea
            className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-white/30 focus:outline-none"
            rows={3}
            placeholder="Ask the council a prediction market question..."
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAsk}
              disabled={ask.isPending || trimmed.length < MIN_QUESTION_LENGTH}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="h-3 w-3" />
              {ask.isPending ? "Querying..." : "Ask Council"}
            </button>
            {tooShort ? (
              <span className="text-xs text-amber-400/80">
                question needs at least {MIN_QUESTION_LENGTH} characters
              </span>
            ) : null}
            {members.data ? (
              <span className="text-xs text-white/30">
                {members.data.members.length} providers + search + judge
              </span>
            ) : members.isError ? (
              <span className="text-xs text-red-400/80">provider list unavailable</span>
            ) : null}
          </div>
        </div>
        {ask.isPending ? <LoadingState text="Querying council..." /> : null}
        {ask.isError ? <p className="text-sm text-red-400">Council request failed.</p> : null}
        {verdict ? <VerdictView verdict={verdict} /> : null}
      </div>
    </Panel>
  );
}
